// React and Third-party
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from "react-helmet";
import pluralize from 'pluralize';
import moment from 'moment-timezone';
import cogoToast from 'cogo-toast';

// Context
import { Consumer } from "../../context";

// Components
import Header from "../../components/header/header";
import Controls from '../../components/controls/controls';
import SidebarAdministracion from '../../components/header/sidebarAdministracion';
import BalancesTable from './components/BalancesTable';
import MovimientosTable from './components/MovimientosTable';
import AbonarGeneralModal from './components/AbonarGeneralModal';

// Requests
import { getInquilinosBalance, getMovimientos } from "./requests";

const loadingType = {
    none: "NONE",
    movimientosGeneral: "MOVS",
    movimientosInquilino: "MOVS-INQ",
    balances: "BALANCES"
}

const initParameters = () => ({
    textFilter: "",
    fraccionamientoFilter: 0,
});

const initSearchParameters = () => ({
    byBalances: true,
    inquilino: 0,
    vivienda: 0,
    catalogo: 0,
    desdeFecha: moment().add(-1, "month").format("YYYY-MM-DD"),
    hastaFecha: moment().format("YYYY-MM-DD")
});

const excelConfigBalances = {
    excelColumnsShow: "inquilino,numero_registro,subfraccionamiento,saldo",
    excelColumnsName: "Residente,Vivienda,Subfraccionamiento,Balance",
    excelColumnsType: "string,string,string,money"
}

const excelConfigMovimientos = {
    excelColumnsShow: "inquilino,vivienda,Subfraccionamiento,cantidad,tipo,descripcion,fecha",
    excelColumnsName: "Residente,Vivienda,Subfraccionamiento,Cantidad,Servicio,Detalle,Fecha",
    excelColumnsType: "string,string,string,money,string,string,date"
}

function TarjetaVirtual(props) {

    const { context, location } = props;

    const [subMenu, setSubMenu] = useState("");
    const [balances, setBalances] = useState([]);
    const [movimientos, setMovimientos] = useState([]);
    const [loadingData, setLoadingData] = useState(loadingType.none);
    const [localParameters, setLocalParameters] = useState(initParameters);
    const [fetchParameters, setFetchParameters] = useState(initSearchParameters);

    const [showAbonarGeneralModal, setShowAbonarGeneralModal] = useState(false);

    const [selectedInquilino, setSelectedInquilino] = useState(null);

    const [inquilinoPreseleccionado, setInquilinoPreseleccionado] = useState(null);

    const loadInquilinosBalance = useCallback(async () => {
        setLoadingData(loadingType.balances);
        const response = await getInquilinosBalance(localStorage.getItem("frac"), 0);
        const { balances, empty, error, message } = response;

        if (error) {
            cogoToast.error(message, { position: "bottom-right", hideAfter: 5 });
        }
        else if (empty) {
            cogoToast.warn(message, { position: "bottom-right", hideAfter: 5 });
        }
        else {
            Object.assign(balances[0], { ...excelConfigBalances });
            setBalances(balances);
        }
        setLoadingData(loadingType.none);
    }, []);

    const loadMovimientos = useCallback(async (loadType, params) => {
        setLoadingData(loadType);
        const { error, movimientos } = await getMovimientos(
            params.id_fraccionamiento,
            params.inquilino,
            params.vivienda,
            params.catalogo,
            params.desdeFecha,
            params.hastaFecha
        );
        if (error) {
            if (movimientos && movimientos.length === 0) {
                cogoToast.warn(error, { position: "bottom-right", hideAfter: 5 });
            } else {
                cogoToast.error(error, { position: "bottom-right", hideAfter: 5 });
            }
        }
        if (movimientos) {
            if (movimientos.length > 0) Object.assign(movimientos[0], { ...excelConfigMovimientos });
            setMovimientos(movimientos);
        }
        setLoadingData(loadingType.none);
    }, []);

    const fetchData = useCallback(() => {
        loadInquilinosBalance();
    }, [loadInquilinosBalance]);

    useEffect(() => {
        const subname = getSubmenuAdminName(location.pathname);
        setSubMenu(subname);
    }, [location]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onParametersChanged = (setParameters, key, value) => {
        setParameters(prevParameters => {
            const parametersCopy = { ...prevParameters }
            parametersCopy[key] = value;
            return parametersCopy;
        });
    };

    const filterByText = (data, filter) => {
        if (!filter || filter === "") return data;
        return data.filter((mov) => {
            const numero = mov.numero_registro?.toLowerCase() || "";
            const inquilino = mov.inquilino?.toLowerCase() || "";
            const sub = mov.subfraccionamiento?.toLowerCase() || "";
            return numero.includes(filter) || inquilino.includes(filter) || sub.includes(filter);
        });
    }

    const filterbyFraccionamiento = (data, filter) => {
        if (filter === 0) return data;
        return data.filter((mov) => mov.id_subfraccionamiento === filter);
    }

    const applyFilters = (parameters, unfilteredData) => {
        const filterText = filterByText(unfilteredData, parameters.textFilter);
        return filterbyFraccionamiento(filterText, parameters.fraccionamientoFilter);
    };

    const data = balances;
    const filteredData = applyFilters(localParameters, data);

    const Historial = useCallback((props) => (
        <MovimientosTable
            {...props}
            movimientos={movimientos}
            header={true}
            byVivienda={true}
            fraccionamiento={context.fraccionamiento}
            usuario={context.usuario}
        />
    ), [context, movimientos]);

    const handleSelectInquilino = (inquilino) => {
        setSelectedInquilino(inquilino); // puede ser null
    };
    const handleAbonarGeneral = () => {
        if (selectedInquilino) {
            setInquilinoPreseleccionado(selectedInquilino);
        } else {
            setInquilinoPreseleccionado(null);
        }
        setShowAbonarGeneralModal(true);
    };

    // 👇 Callback cuando se cierra el modal
    const handleCloseModal = () => {
        setShowAbonarGeneralModal(false);
        // No limpiamos la selección para mantenerla visible
    };

    // 👇 Callback cuando el abono es exitoso
    const handleAbonoSuccess = () => {
        // Limpiar selección y recargar datos
        setSelectedInquilino(null);
        fetchData();
    };

    return (
        <div className="column">
            <Helmet>
                <title>{process.env.REACT_APP_NAME} - {subMenu}</title>
            </Helmet>
            <Header
                id='header'
                reload={() => console.log("Add reload")}
                change={() => console.log("Add change")}
                sidebar={true}
                nav={"administracion"}
            />
            <div className="row full">
                <div className="column" id="sidebar">
                    <SidebarAdministracion />
                </div>
                <div className="column align-center" id="content">
                    <div className="container column">
                        <div className="row" style={{ alignItems: 'center', width: '100%' }}>
                            <Controls
                                title={"Tarjeta Virtual - Historial"}
                                dataSet={data}
                                dateFormat={"dd/mm/yyyy HH:mm:ss"}
                                setFilter={(value) => onParametersChanged(setLocalParameters, "textFilter", value.toLowerCase())}
                                subChange={(sub) => onParametersChanged(setLocalParameters, "fraccionamientoFilter", +sub)}
                                filterValue={localParameters.textFilter}
                            />
                            <button
                                className="btn btn-primary btn-red-table color-white"
                                style={{ marginLeft: 'auto', marginRight: '16px', marginBottom: '4%' }}
                                onClick={handleAbonarGeneral}
                            >
                                Abonar
                            </button>
                        </div>

                        <BalancesTable
                            key={`balances-${localParameters.textFilter}-${localParameters.fraccionamientoFilter}`}
                            balances={loadingData === loadingType.none || loadingData === loadingType.movimientosInquilino ? filteredData : []}
                            loadingBalances={loadingData === loadingType.balances}
                            loadingInquilinosMovimientos={loadingData === loadingType.movimientosInquilino}
                            desdeFecha={fetchParameters.desdeFecha}
                            hastaFecha={fetchParameters.hastaFecha}
                            id_catalogo={fetchParameters.catalogo}
                            onMovimientosSearch={(inquilino) => loadMovimientos(loadingType.movimientosInquilino, { id_fraccionamiento: localStorage.getItem("frac"), ...fetchParameters, inquilino })}
                            onAbonoCompleted={() => fetchData()}
                            HistorialComponent={Historial}
                            selectedInquilino={selectedInquilino}
                            onSelectInquilino={handleSelectInquilino}
                        />
                    </div>
                </div>
            </div>

            {/* Modal de Abono (único) */}
            <AbonarGeneralModal
                isOpen={showAbonarGeneralModal}
                onClose={handleCloseModal}
                onAbonoSuccess={handleAbonoSuccess}
                inquilinoPreseleccionado={inquilinoPreseleccionado}
            />
        </div>
    )
}

export default Consumer(TarjetaVirtual);

const getSubmenuAdminName = (route) => {
    let name = window.localStorage.getItem('submenu_name_admin');
    if (!name) {
        let catalogos_menus = window.localStorage.getItem('catalogos_menus');
        if (catalogos_menus) {
            catalogos_menus = JSON.parse(catalogos_menus);
            if (catalogos_menus.length !== 0) {
                name = catalogos_menus[0].recordset[0].nombre;
                let singular = pluralize.isPlural(name);
                if (singular) {
                    name = pluralize.singular(name);
                }
                window.localStorage.setItem('subLabel', name);
            }
        }

        let menus = window.localStorage.getItem('administracion_menus');
        menus = JSON.parse(menus);
        if (menus.length !== 0) {
            let submenu;
            for (let index = 0; index < menus.length; index++) {
                const subs = menus[index].recordset;
                const foundSub = subs.find(sub => sub.ruta == route);
                if (foundSub) {
                    submenu = foundSub;
                    break;
                }
            }
            if (submenu) {
                window.localStorage.setItem('submenu_admin', submenu.id_menu);
                window.localStorage.setItem('submenu_name_admin', submenu.nombre);
                name = submenu.nombre;
            } else {
                window.localStorage.setItem('submenu_admin', menus[0].recordset[0].id_menu);
                window.localStorage.setItem('submenu_name_admin', menus[0].recordset[0].nombre);
                window.location = `${menus[0].recordset[0].ruta}`;
                name = menus[0].recordset[0].nombre;
            }
        }
    }
    return name;
}